import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, InputLabel, FormControl, Alert } from '@mui/material';
import { ActiveAdSpaceInterface } from '../../../interfaces/interfaces';

interface Props {
  open: boolean;
  adSpaceToUpdate: ActiveAdSpaceInterface | null;
  setAdSpaceToUpdate: React.Dispatch<React.SetStateAction<ActiveAdSpaceInterface | null>>;
  updateForm: Partial<ActiveAdSpaceInterface>;
  setUpdateForm: React.Dispatch<React.SetStateAction<Partial<ActiveAdSpaceInterface>>>;
  onClose: () => void;
  onSave: () => void;
  updateError?: string | null;
  updateSuccess?: string | null;
}

export const ActiveAdSpaceUpdateDialog: React.FC<Props> = ({
  open,
  adSpaceToUpdate,
  setAdSpaceToUpdate,
  updateForm,
  setUpdateForm,
  onClose,
  onSave,
  updateError,
  updateSuccess,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Update Ad Space</DialogTitle>
    <DialogContent>
        {updateError && <Alert severity="error" sx={{ mb: 2 }}>{updateError}</Alert>}
        {updateSuccess && <Alert severity="success" sx={{ mb: 2 }}>{updateSuccess}</Alert>}
        <TextField
            label="Geo Name"
            value={updateForm.geoName ?? adSpaceToUpdate?.geoName ?? ''}
            onChange={e => setUpdateForm(f => ({ ...f, geoName: e.target.value }))}
            fullWidth
            margin="normal"
        />
        <TextField
            label="Total Amount To Be Paid"
            type="number"
            value={updateForm.totalAmountToBePaid ?? adSpaceToUpdate?.totalAmountToBePaid ?? ''}
            onChange={e => setUpdateForm(f => ({ ...f, totalAmountToBePaid: Number(e.target.value) }))}
            fullWidth
            margin="normal"
        />
        <TextField
            label="Total Amount Paid So Far"
            type="number"
            value={updateForm.totalAmountPaidSoFar ?? adSpaceToUpdate?.totalAmountPaidSoFar ?? ''}
            onChange={e => setUpdateForm(f => ({ ...f, totalAmountPaidSoFar: Number(e.target.value) }))}
            fullWidth
            margin="normal"
        />
        <TextField
            label="Total Amount Remaining"
            type="number"
            value={updateForm.totalAmountRemaining ?? adSpaceToUpdate?.totalAmountRemaining ?? ''}
            onChange={e => setUpdateForm(f => ({ ...f, totalAmountRemaining: Number(e.target.value) }))}
            fullWidth
            margin="normal"
        />
        <FormControl fullWidth margin="normal">
            <InputLabel id="isFullyPaidToAdSpaceOwner-label">Fully Paid To Owner</InputLabel>
            <Select
                labelId="isFullyPaidToAdSpaceOwner-label"
                value={
                    updateForm.isFullyPaidToAdSpaceOwner !== undefined
                        ? updateForm.isFullyPaidToAdSpaceOwner ? 'yes' : 'no'
                        : adSpaceToUpdate?.isFullyPaidToAdSpaceOwner ? 'yes' : 'no'
                }
                label="Fully Paid To Owner"
                onChange={e =>
                    setUpdateForm(f => ({
                        ...f,
                        isFullyPaidToAdSpaceOwner: e.target.value === 'yes'
                    }))
                }
            >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
            <InputLabel id="weReceivedPayment-label">We Received Payment</InputLabel>
            <Select
                labelId="weReceivedPayment-label"
                value={
                    updateForm.weReceivedPayment !== undefined
                        ? updateForm.weReceivedPayment ? 'yes' : 'no'
                        : adSpaceToUpdate?.weReceivedPayment ? 'yes' : 'no'
                }
                label="We Received Payment"
                onChange={e =>
                    setUpdateForm(f => ({
                        ...f,
                        weReceivedPayment: e.target.value === 'yes'
                    }))
                }
            >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </Select>
        </FormControl>
        <TextField
            label="Payment Transaction ID"
            value={updateForm.paymentTransactionId ?? adSpaceToUpdate?.paymentTransactionId ?? ''}
            onChange={e => setUpdateForm(f => ({ ...f, paymentTransactionId: e.target.value }))}
            fullWidth
            margin="normal"
        />
        <FormControl fullWidth margin="normal">
            <InputLabel id="isVerifiedAtHalfTime-label">Verified Half Time</InputLabel>
            <Select
                labelId="isVerifiedAtHalfTime-label"
                value={
                    updateForm.isVerifiedAtHalfTime !== undefined
                        ? updateForm.isVerifiedAtHalfTime ? 'yes' : 'no'
                        : adSpaceToUpdate?.isVerifiedAtHalfTime ? 'yes' : 'no'
                }
                label="Verified Half Time"
                onChange={e =>
                    setUpdateForm(f => ({
                        ...f,
                        isVerifiedAtHalfTime: e.target.value === 'yes'
                    }))
                }
            >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </Select>
        </FormControl>
        <TextField
            label="Half Time Date"
            type="datetime-local"
            value={
                updateForm.isVerifiedAtHalfTimeDate
                    ? new Date(updateForm.isVerifiedAtHalfTimeDate).toISOString().slice(0, 16)
                    : adSpaceToUpdate?.isVerifiedAtHalfTimeDate
                        ? new Date(adSpaceToUpdate.isVerifiedAtHalfTimeDate).toISOString().slice(0, 16)
                        : ''
            }
            onChange={e => setUpdateForm(f => ({ ...f, isVerifiedAtHalfTimeDate: e.target.value }))}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="normal">
            <InputLabel id="isVerifiedAtFullTime-label">Verified Full Time</InputLabel>
            <Select
                labelId="isVerifiedAtFullTime-label"
                value={
                    updateForm.isVerifiedAtFullTime !== undefined
                        ? updateForm.isVerifiedAtFullTime ? 'yes' : 'no'
                        : adSpaceToUpdate?.isVerifiedAtFullTime ? 'yes' : 'no'
                }
                label="Verified Full Time"
                onChange={e =>
                    setUpdateForm(f => ({
                        ...f,
                        isVerifiedAtFullTime: e.target.value === 'yes'
                    }))
                }
            >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </Select>
        </FormControl>
        <TextField
            label="Full Time Date"
            type="datetime-local"
            value={
                updateForm.isVerifiedAtFullTimeDate
                    ? new Date(updateForm.isVerifiedAtFullTimeDate).toISOString().slice(0, 16)
                    : adSpaceToUpdate?.isVerifiedAtFullTimeDate
                        ? new Date(adSpaceToUpdate.isVerifiedAtFullTimeDate).toISOString().slice(0, 16)
                        : ''
            }
            onChange={e => setUpdateForm(f => ({ ...f, isVerifiedAtFullTimeDate: e.target.value }))}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="normal">
            <InputLabel id="waitingForApproval-label">Waiting For Approval</InputLabel>
            <Select
                labelId="waitingForApproval-label"
                value={
                    updateForm.waitingForApproval !== undefined
                        ? updateForm.waitingForApproval ? 'yes' : 'no'
                        : adSpaceToUpdate?.waitingForApproval ? 'yes' : 'no'
                }
                label="Waiting For Approval"
                onChange={e =>
                    setUpdateForm(f => ({
                        ...f,
                        waitingForApproval: e.target.value === 'yes'
                    }))
                }
            >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </Select>
        </FormControl>
        <TextField
            label="Approval Date"
            type="datetime-local"
            value={
                updateForm.approvalDate
                    ? new Date(updateForm.approvalDate).toISOString().slice(0, 16)
                    : adSpaceToUpdate?.approvalDate
                        ? new Date(adSpaceToUpdate.approvalDate).toISOString().slice(0, 16)
                        : ''
            }
            onChange={e => setUpdateForm(f => ({ ...f, approvalDate: e.target.value }))}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="normal">
            <InputLabel id="waitingForPayment-label">Waiting For Payment</InputLabel>
            <Select
                labelId="waitingForPayment-label"
                value={
                    updateForm.waitingForPayment !== undefined
                        ? updateForm.waitingForPayment ? 'yes' : 'no'
                        : adSpaceToUpdate?.waitingForPayment ? 'yes' : 'no'
                }
                label="Waiting For Payment"
                onChange={e =>
                    setUpdateForm(f => ({
                        ...f,
                        waitingForPayment: e.target.value === 'yes'
                    }))
                }
            >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </Select>
        </FormControl>
        <TextField
            label="Payment Date"
            type="datetime-local"
            value={
                updateForm.paymentDate
                    ? new Date(updateForm.paymentDate).toISOString().slice(0, 16)
                    : adSpaceToUpdate?.paymentDate
                        ? new Date(adSpaceToUpdate.paymentDate).toISOString().slice(0, 16)
                        : ''
            }
            onChange={e => setUpdateForm(f => ({ ...f, paymentDate: e.target.value }))}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="normal">
            <InputLabel id="active-label">Active</InputLabel>
            <Select
                labelId="active-label"
                value={
                    updateForm.active !== undefined
                        ? updateForm.active
                            ? 'yes'
                            : 'no'
                        : adSpaceToUpdate?.active
                            ? 'yes'
                            : 'no'
                }
                label="Active"
                onChange={e =>
                    setUpdateForm(f => ({
                        ...f,
                        active: e.target.value === 'yes'
                    }))
                }
            >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
            </Select>
        </FormControl>
        <TextField
            label="Start Date"
            type="datetime-local"
            value={
                updateForm.startDate
                    ? new Date(updateForm.startDate).toISOString().slice(0, 16)
                    : adSpaceToUpdate?.startDate
                        ? new Date(adSpaceToUpdate.startDate).toISOString().slice(0, 16)
                        : ''
            }
            onChange={e => setUpdateForm(f => ({ ...f, startDate: e.target.value }))}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
        />
        <TextField
            label="End Date"
            type="datetime-local"
            value={
                updateForm.endDate
                    ? new Date(updateForm.endDate).toISOString().slice(0, 16)
                    : adSpaceToUpdate?.endDate
                        ? new Date(adSpaceToUpdate.endDate).toISOString().slice(0, 16)
                        : ''
            }
            onChange={e => setUpdateForm(f => ({ ...f, endDate: e.target.value }))}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
        />
      
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="primary" onClick={onSave}>Update</Button>
    </DialogActions>
  </Dialog>
);